/**
 * Student profile — Genel Bakış tab.
 *
 * A read-only summary: the same stat tiles the profile always had, plus a
 * miniature of the Ölçümler tab's own charts and a short recent-history
 * table, so opening a student answers "how are they doing" without a click.
 * Adding or editing a measurement still only happens on the Ölçümler tab —
 * this tab never writes anything.
 */

import {
  buildCharts,
  calculateBmi,
  customMetricSeries,
  customMetricsOf,
  formatDateShort,
  formatValue,
} from '@pp/shared';
import type { MeasurementListResponse } from '../api/types.js';
import type { StudentMetric } from '../api/types.js';
import { Chart } from '../components/Chart.js';
import { MetricTile, type TileMetric } from '../components/MetricTile.js';
import { Button } from '../components/ui/Button.js';
import { useApiData } from '../hooks/useApiData.js';
import { toRecord } from './MeasurementsTab.js';

/**
 * The same current/previous/change shape the fixed metrics arrive in
 * (StudentMetric, computed server-side), built client-side for a studio's
 * own custom field — nothing resolves these server-side today.
 */
function summarizeCustomMetric(
  records: ReturnType<typeof toRecord>[],
  labelNormalized: string,
  label: string,
  unit: string,
): TileMetric | null {
  const series = customMetricSeries(records, labelNormalized);
  if (series.length === 0) return null;
  const current = series[series.length - 1]!;
  const previous = series.length > 1 ? series[series.length - 2]! : null;
  return {
    label,
    unit,
    decimals: 1,
    currentValue: current.value,
    currentDate: current.measuredAt,
    previousValue: previous?.value ?? null,
    previousDate: previous?.measuredAt ?? null,
    changeAbs: previous ? current.value - previous.value : null,
    blockedReason: null,
  };
}

/** Most recent rows first, newest few only — a glance, not the full log
    (that's what the Ölçümler tab is for). */
const RECENT_ROWS = 5;
/** Enough to fill a row without crowding a narrow profile panel. */
const SUMMARY_CHARTS = 3;

export function OverviewTab({
  studentId,
  heightCm,
  metrics,
  onAddMeasurement,
}: {
  studentId: string;
  heightCm: number;
  metrics: StudentMetric[];
  onAddMeasurement: () => void;
}) {
  const { data } = useApiData<MeasurementListResponse>(
    `/api/students/${studentId}/measurements`,
  );
  const items = data?.items ?? [];
  const records = items.map(toRecord);

  // The studio's own fields get a tile too — otherwise a custom metric is
  // visible nowhere above the full measurement log.
  const customTiles = customMetricsOf(records)
    .map((metric) => ({
      labelNormalized: metric.labelNormalized,
      tile: summarizeCustomMetric(
        records,
        metric.labelNormalized,
        metric.label,
        metric.unit,
      ),
    }))
    .filter(
      (entry): entry is { labelNormalized: string; tile: TileMetric } =>
        entry.tile !== null,
    );

  // A student measured on nothing but a custom field still has data worth
  // showing — metrics alone being empty is not the same as having nothing.
  if (metrics.length === 0 && customTiles.length === 0) {
    return (
      <div className="profile__empty">
        <p>Bu öğrenci için henüz ölçüm girilmemiş.</p>
        <Button type="button" onClick={onAddMeasurement}>
          Ölçüm Ekle
        </Button>
      </div>
    );
  }

  const charts = buildCharts(records, heightCm).slice(0, SUMMARY_CHARTS);
  // Newest first, same convention as the rest of the profile.
  const recentRows = [...items]
    .sort((a, b) => (a.measuredAt < b.measuredAt ? 1 : -1))
    .slice(0, RECENT_ROWS);

  return (
    <>
      <h2 className="profile__section">Son Ölçüm Değerleri</h2>
      <div className="profile__tiles">
        {metrics.map((metric) => (
          <MetricTile key={metric.key} metric={metric} />
        ))}
        {customTiles.map(({ labelNormalized, tile }) => (
          <MetricTile key={labelNormalized} metric={tile} />
        ))}
      </div>

      {charts.length > 0 && (
        <>
          <h2 className="profile__section profile__section--spaced">
            Ölçüm Gelişim Grafiği
          </h2>
          <div className="profile__charts">
            {charts.map((chart) => (
              <Chart key={chart.metricKey} chart={chart} />
            ))}
          </div>
        </>
      )}

      {recentRows.length > 0 && (
        <>
          <h2 className="profile__section profile__section--spaced">Son Ölçümler</h2>
          <div className="profile__table-wrap">
            <table className="profile__table">
              <thead>
                <tr>
                  <th scope="col">Tarih</th>
                  <th scope="col">Kilo</th>
                  <th scope="col">Bel</th>
                  <th scope="col">Kalça</th>
                  <th scope="col">Göğüs</th>
                  <th scope="col">BMI</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.map((row) => {
                  const bmi =
                    row.weightKg !== null ? calculateBmi(row.weightKg, heightCm) : null;
                  return (
                    <tr key={row.id}>
                      <td>{formatDateShort(row.measuredAt)}</td>
                      <td>
                        {row.weightKg !== null ? formatValue(row.weightKg, 'kg', 1) : '—'}
                      </td>
                      <td>
                        {row.waistCm !== null ? formatValue(row.waistCm, 'cm', 1) : '—'}
                      </td>
                      <td>
                        {row.hipCm !== null ? formatValue(row.hipCm, 'cm', 1) : '—'}
                      </td>
                      <td>
                        {row.chestCm !== null ? formatValue(row.chestCm, 'cm', 1) : '—'}
                      </td>
                      <td>{bmi !== null ? formatValue(bmi, '', 1) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
